"use client";
import React, { useEffect, useRef, useState } from 'react';
import { FiMic, FiMicOff, FiVideo, FiVideoOff, FiPhoneOff } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

/**
 * CallScreen component for browser-native WebRTC Video Call
 * 
 * Props:
 * - conversationId: string
 * - myId: string
 * - otherUserName: string
 * - isCaller: boolean
 * - onEnd: () => void
 */
const CallScreen = ({
  conversationId,
  myId,
  otherUserName = 'المستخدم',
  isCaller = false,
  onEnd
}) => {
  const [localStream, setLocalStream] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callStatus, setCallStatus] = useState(isCaller ? 'جاري الاتصال...' : 'جاري الربط...');
  const [callDuration, setCallDuration] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const timerRef = useRef(null);

  const SIGNALING_TOKEN = 'Kh1dma_S3cur3_C@ll_2026_XZ9';
  const SIGNALING_URL = 'wss://khidma-signaling.mouhoubifares12.workers.dev';

  // 1. Initialize Media, PeerConnection, and WebSocket
  useEffect(() => {
    let isMounted = true;

    const getMediaStream = async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('SECURE_CONTEXT_REQUIRED');
      }

      // Attempt 1: Video with facingMode user + Audio
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { facingMode: 'user' }
        });
      } catch (err1) {
        console.warn('Media attempt 1 (facingMode user) failed:', err1);
      }

      // Attempt 2: Simple Video + Audio
      try {
        return await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });
      } catch (err2) {
        console.warn('Media attempt 2 (simple video) failed:', err2);
      }

      // Attempt 3: Audio only
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false
        });
        setIsVideoOff(true);
        toast('تم بدء المكالمة بالصوت فقط (الكاميرا غير متاحة)', { icon: '🎙️' });
        return audioStream;
      } catch (err3) {
        console.error('Media attempt 3 (audio only) failed:', err3);
        throw err3;
      }
    };

    const initCall = async () => {
      try {
        console.log('🎥 Initializing media stream...');
        const stream = await getMediaStream();

        if (!isMounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        localStreamRef.current = stream;
        setLocalStream(stream);

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // ج) إنشاء RTCPeerConnection
        const configuration = {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        };
        const pc = new RTCPeerConnection(configuration);
        pcRef.current = pc;

        // إعداد البث البعيد
        const remoteStream = new MediaStream();
        remoteStreamRef.current = remoteStream;
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }

        // إضافة الـ tracks المحلية للـ peer connection
        stream.getTracks().forEach((track) => {
          pc.addTrack(track, stream);
        });

        // التعامل مع البث البعيد قادم من الطرف الآخر
        pc.ontrack = (event) => {
          console.log('🎵 Remote track received');
          if (event.streams && event.streams[0]) {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = event.streams[0];
            }
          } else {
            event.tracks.forEach((track) => {
              remoteStreamRef.current.addTrack(track);
            });
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = remoteStreamRef.current;
            }
          }
          setIsConnected(true);
          setCallStatus('متصل الآن');
        };

        // التعامل مع مرشحي ICE إرسالهم للوركر
        pc.onicecandidate = (event) => {
          if (event.candidate && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            console.log('📤 Sending ICE Candidate');
            wsRef.current.send(
              JSON.stringify({
                type: 'candidate',
                candidate: event.candidate
              })
            );
          }
        };

        pc.onconnectionstatechange = () => {
          console.log('🔄 PeerConnection state:', pc.connectionState);
          if (pc.connectionState === 'connected') {
            setIsConnected(true);
            setCallStatus('متصل الآن');
          } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
            setCallStatus('انقطع الاتصال');
          }
        };

        // ب) الاتصال بالوركر عبر WebSocket
        const wsRoom = encodeURIComponent(conversationId);
        const wsUser = encodeURIComponent(myId);
        const fullWsUrl = `${SIGNALING_URL}?room=${wsRoom}&userId=${wsUser}&t=${SIGNALING_TOKEN}`;

        console.log('🔌 Connecting to WebSocket signaling server...');
        const ws = new WebSocket(fullWsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('✅ WebSocket Connected');
          
          // إذا isCaller=true: بعد نصف ثانية من فتح الاتصال، أنشئ offer عبر createOffer وsetLocalDescription وابعثه
          if (isCaller) {
            setTimeout(async () => {
              if (pcRef.current && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                try {
                  console.log('📤 Creating Offer...');
                  const offer = await pcRef.current.createOffer();
                  await pcRef.current.setLocalDescription(offer);
                  wsRef.current.send(
                    JSON.stringify({
                      type: 'offer',
                      offer: offer
                    })
                  );
                } catch (err) {
                  console.error('Error creating offer:', err);
                }
              }
            }, 500);
          }
        };

        // د) منطق تبادل الإشارات
        ws.onmessage = async (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📩 Received WebSocket message:', data.type);

            if (data.type === 'offer') {
              // عند استقبال "offer": setRemoteDescription، ثم createAnswer، ثم setLocalDescription، ثم ابعث الـ answer
              const offerData = data.offer || data.sdp || data;
              await pc.setRemoteDescription(new RTCSessionDescription(offerData));
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              if (ws.readyState === WebSocket.OPEN) {
                console.log('📤 Sending Answer...');
                ws.send(
                  JSON.stringify({
                    type: 'answer',
                    answer: answer
                  })
                );
              }
            } else if (data.type === 'answer') {
              // عند استقبال "answer": setRemoteDescription فقط
              const answerData = data.answer || data.sdp || data;
              await pc.setRemoteDescription(new RTCSessionDescription(answerData));
              setIsConnected(true);
              setCallStatus('متصل الآن');
            } else if (data.type === 'candidate') {
              // عند استقبال "candidate": addIceCandidate بالبيانات المستلمة
              if (data.candidate) {
                await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
              }
            } else if (data.type === 'peer_left') {
              // عند استقبال "peer_left": استدعِ onEnd()
              toast.error('أنهى الطرف الآخر المكالمة');
              cleanupAndEnd();
            }
          } catch (err) {
            console.error('Error handling WebSocket message:', err);
          }
        };

        ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          setCallStatus('خطأ في الاتصال بالخادم');
        };

        ws.onclose = () => {
          console.log('🔌 WebSocket connection closed');
        };

      } catch (error) {
        console.error('Error initializing call:', error);
        if (error.message === 'SECURE_CONTEXT_REQUIRED') {
          toast.error('يتطلب الوصول للكاميرا والميكروفون اتصالاً آمناً (HTTPS) أو localhost');
        } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          toast.error('تم رفض إذن الكاميرا/الميكروفون. يرجى السماح بالصلاحيات في إعدادات المتصفح');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          toast.error('لم يتم العثور على كاميرا أو ميكروفون متصل بالجهاز');
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          toast.error('الكاميرا أو الميكروفون مستخدم بواسطة تطبيق آخر');
        } else {
          toast.error(`تعذر الوصول للكاميرا والميكروفون: ${error.message || 'خطأ غير معروف'}`);
        }
        if (onEnd) onEnd();
      }
    };

    initCall();

    // 3) تنظيف الموارد (Cleanup)
    return () => {
      isMounted = false;
      cleanupResources();
    };
  }, [conversationId, myId, isCaller]);

  // Timer for duration when connected
  useEffect(() => {
    if (isConnected) {
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isConnected]);

  const cleanupResources = () => {
    if (timerRef.current) clearInterval(timerRef.current);

    // إغلاق WebSocket
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        console.error(e);
      }
      wsRef.current = null;
    }

    // إيقاف كل الـ media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }

    // إغلاق RTCPeerConnection
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (e) {
        console.error(e);
      }
      pcRef.current = null;
    }
  };

  const cleanupAndEnd = () => {
    cleanupResources();
    if (onEnd) onEnd();
  };

  // زر كتم الصوت
  const toggleAudio = () => {
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getAudioTracks();
      const nextState = !isAudioMuted;
      tracks.forEach((track) => {
        track.enabled = !nextState;
      });
      setIsAudioMuted(nextState);
    }
  };

  // زر الكاميرا
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getVideoTracks();
      const nextState = !isVideoOff;
      tracks.forEach((track) => {
        track.enabled = !nextState;
      });
      setIsVideoOff(nextState);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col justify-between overflow-hidden select-none">
      {/* 1. الفيديو البعيد (كبير - ملء الشاشة تقريبا) */}
      <div className="relative w-full h-full flex items-center justify-center bg-gray-950 overflow-hidden">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="w-full h-full object-cover"
        />

        {!isConnected && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md flex flex-col items-center justify-center p-6 text-white text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-3xl font-bold mb-4 shadow-xl animate-pulse">
              {otherUserName ? otherUserName.charAt(0).toUpperCase() : '؟'}
            </div>
            <h2 className="text-2xl font-bold mb-2">{otherUserName}</h2>
            <p className="text-gray-300 text-sm font-medium animate-pulse">{callStatus}</p>
          </div>
        )}

        {/* 2. شريط علوي للمعلومات */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent flex items-center justify-between text-white z-20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center font-bold text-sm shadow">
              {otherUserName ? otherUserName.charAt(0).toUpperCase() : '؟'}
            </div>
            <div>
              <h3 className="font-semibold text-sm leading-tight">{otherUserName}</h3>
              <p className="text-xs text-gray-300">
                {isConnected ? formatDuration(callDuration) : callStatus}
              </p>
            </div>
          </div>
        </div>

        {/* 3. الفيديو المحلي الصغير (في زاوية الشاشة معكوس بصرياً) */}
        <motion.div
          drag
          dragConstraints={{ top: 10, left: 10, right: 200, bottom: 400 }}
          className="absolute top-16 right-4 w-28 h-40 sm:w-36 sm:h-52 bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 z-30 cursor-move"
        >
          <video
            ref={localVideoRef}
            muted
            autoPlay
            playsInline
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          {isVideoOff && (
            <div className="absolute inset-0 bg-gray-900 flex flex-col items-center justify-center text-white">
              <FiVideoOff className="w-6 h-6 text-gray-400 mb-1" />
              <span className="text-[10px] text-gray-400">الكاميرا مغلقة</span>
            </div>
          )}
        </motion.div>
      </div>

      {/* 4. أزرار تحكم أسفل الشاشة */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-center justify-center gap-6 z-40">
        {/* زر كتم/تشغيل الصوت */}
        <button
          onClick={toggleAudio}
          className={`p-4 rounded-full transition-all duration-200 text-white shadow-lg ${
            isAudioMuted
              ? 'bg-red-500 hover:bg-red-600 ring-4 ring-red-500/30'
              : 'bg-white/20 hover:bg-white/30 backdrop-blur-md'
          }`}
          title={isAudioMuted ? 'تشغيل الصوت' : 'كتم الصوت'}
        >
          {isAudioMuted ? <FiMicOff className="w-6 h-6" /> : <FiMic className="w-6 h-6" />}
        </button>

        {/* زر الإنهاء (أحمر) */}
        <button
          onClick={cleanupAndEnd}
          className="p-5 rounded-full bg-red-600 hover:bg-red-700 active:scale-95 transition-all duration-200 text-white shadow-xl ring-4 ring-red-600/40"
          title="إنهاء المكالمة"
        >
          <FiPhoneOff className="w-7 h-7" />
        </button>

        {/* زر تشغيل/إيقاف الكاميرا */}
        <button
          onClick={toggleVideo}
          className={`p-4 rounded-full transition-all duration-200 text-white shadow-lg ${
            isVideoOff
              ? 'bg-red-500 hover:bg-red-600 ring-4 ring-red-500/30'
              : 'bg-white/20 hover:bg-white/30 backdrop-blur-md'
          }`}
          title={isVideoOff ? 'تشغيل الكاميرا' : 'إيقاف الكاميرا'}
        >
          {isVideoOff ? <FiVideoOff className="w-6 h-6" /> : <FiVideo className="w-6 h-6" />}
        </button>
      </div>
    </div>
  );
};

export default CallScreen;
